import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Delete,
  Image as ImageIcon,
  InsertDriveFile,
  PictureAsPdf,
  Preview,
  Psychology,
  Refresh,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import {
  prescriptionUploadAPI,
  type PrescriptionUploadImage,
  type PrescriptionUploadMedication,
  type PrescriptionUploadSummary,
} from '../../services/api';
import { usePrescriptionUpload, type UploadedPrescriptionFile } from '../../contexts/PrescriptionUploadContext';
import { useSession } from '../../contexts/SessionContext';
import { useUser } from '../../contexts/UserContext';
import ImageGalleryDialog from '../LabReportUpload/ImageGalleryDialog';
import VisionAIUploadDropzone from '../Common/VisionAIUploadDropzone';
import { formatFileSize, isPdfOrImageFile } from '../Common/visionUploadUtils';

const EMPTY_ROW: PrescriptionUploadMedication = {
  drug_name: '',
  dose: '',
  unit: '',
  frequency: '',
  route: '',
  duration: '',
  medication_instructions: '',
};

const PRESCRIPTION_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.bmp,.tiff';
const PRESCRIPTION_FORMATS = 'PDF, JPG, JPEG, PNG, WEBP, BMP, TIFF';

type Props = {
  sessionId: string | null;
  patientId?: number | string | null;
  onApproved: (rows: PrescriptionUploadMedication[]) => void;
  onCancel: () => void;
};

function prescriptionDisplayName(upload: PrescriptionUploadSummary) {
  const stem = (upload.filename || 'Prescription').replace(/\.[^/.]+$/, '');
  const d = upload.created_at
    ? new Date(upload.created_at).toISOString().slice(0, 10)
    : 'unknown';
  const ext = (upload.file_type || 'pdf').toLowerCase();
  return `${stem}_Rx_${d}.${ext}`;
}

export default function PrescriptionUploadPanel({
  sessionId,
  patientId: patientIdProp,
  onApproved,
  onCancel,
}: Props) {
  const { files, addFile, updateFile, removeFile } = usePrescriptionUpload();
  const { session, formSessionOverride } = useSession();
  const { patient } = useUser();

  const activeSession = formSessionOverride ?? session;
  const resolvedPatientId =
    patientIdProp ?? activeSession?.patient_id ?? patient?.patient_id ?? null;

  const [currentTab, setCurrentTab] = useState(0);
  const [sessionUploads, setSessionUploads] = useState<PrescriptionUploadSummary[]>([]);
  const [patientUploads, setPatientUploads] = useState<PrescriptionUploadSummary[]>([]);
  const [loadingSession, setLoadingSession] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);

  const [activeUploadId, setActiveUploadId] = useState<string | null>(null);
  const [rows, setRows] = useState<PrescriptionUploadMedication[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [imageGallery, setImageGallery] = useState<{
    open: boolean;
    images: PrescriptionUploadImage[];
  }>({ open: false, images: [] });

  const hasRows = useMemo(() => rows.length > 0, [rows]);
  const isUploading = files.some((f) => f.status === 'uploading' || f.status === 'processing');

  const fetchSessionUploads = useCallback(async () => {
    if (!sessionId) {
      setSessionUploads([]);
      return;
    }
    setLoadingSession(true);
    try {
      const response = await prescriptionUploadAPI.getSessionPrescriptionUploads(sessionId);
      setSessionUploads(response.data.uploads || []);
    } catch {
      console.error('Failed to fetch session prescription uploads');
    } finally {
      setLoadingSession(false);
    }
  }, [sessionId]);

  const fetchPatientUploads = useCallback(async () => {
    if (resolvedPatientId == null || String(resolvedPatientId).trim() === '') {
      setPatientUploads([]);
      return;
    }
    setLoadingPatient(true);
    try {
      const response = await prescriptionUploadAPI.getAllPrescriptionUploads({
        patient_id: resolvedPatientId,
        limit: 100,
      });
      setPatientUploads(response.data.uploads || []);
    } catch {
      console.error('Failed to fetch patient prescription uploads');
    } finally {
      setLoadingPatient(false);
    }
  }, [resolvedPatientId]);

  useEffect(() => {
    if (sessionId) void fetchSessionUploads();
  }, [sessionId, fetchSessionUploads]);

  useEffect(() => {
    void fetchPatientUploads();
  }, [fetchPatientUploads]);

  const sessionContextIds = new Set(
    files.map((f) => f.upload_id).filter(Boolean) as string[],
  );
  const persistedSessionUploads = sessionUploads.filter(
    (u) => !sessionContextIds.has(u.upload_id),
  );

  const loadUploadForReview = async (uploadId: string) => {
    setError(null);
    try {
      const response = await prescriptionUploadAPI.getPrescriptionUpload(uploadId);
      const meds = response.data.medications ?? [];
      setActiveUploadId(uploadId);
      setRows(meds.length ? meds : [{ ...EMPTY_ROW }]);
      setWarnings([]);
    } catch {
      setError('Failed to load prescription for review.');
    }
  };

  const uploadPrescriptionFile = async (actualFile: File, fileState: UploadedPrescriptionFile) => {
    try {
      const formData = new FormData();
      formData.append('file', actualFile);
      if (sessionId) formData.append('session_id', sessionId);
      if (resolvedPatientId != null) {
        formData.append('patient_id', String(resolvedPatientId));
      }

      updateFile(fileState.id, { status: 'uploading', progress: 0 });
      const response = await prescriptionUploadAPI.uploadPrescription(formData, (percent) => {
        updateFile(fileState.id, {
          status: percent < 100 ? 'uploading' : 'processing',
          progress: percent < 100 ? percent : 95,
        });
      });

      const uploadId = response.data.upload_id;
      const meds = response.data.medications ?? [];
      const apiWarnings = response.data.warnings ?? [];
      const rejected =
        response.data.success === false ||
        Boolean(response.data.user_message) ||
        Boolean(response.data.error_code) ||
        (meds.length === 0 && apiWarnings.length > 0);

      if (rejected) {
        updateFile(fileState.id, { status: 'error', progress: 0 });
        setError(
          response.data.user_message ||
            (apiWarnings.length > 0
              ? apiWarnings.join(' ')
              : 'This image does not look like a prescription. Upload a clear photo of the prescription paper.'),
        );
        return;
      }

      updateFile(fileState.id, {
        status: 'completed',
        progress: 100,
        upload_id: uploadId,
        confidence: response.data.confidence,
        review_status: response.data.review_status,
      });
      setActiveUploadId(uploadId);
      setRows(meds.length ? meds : [{ ...EMPTY_ROW }]);
      setWarnings(apiWarnings);
      setError(null);
      void fetchSessionUploads();
      void fetchPatientUploads();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string; user_message?: string } } })?.response?.data
          ?.user_message ||
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      updateFile(fileState.id, { status: 'error', progress: 0 });
      setError(msg || 'Failed to upload and extract prescription.');
    }
  };

  const handleFiles = async (selectedFiles: File[]) => {
    const validFiles = selectedFiles.filter(isPdfOrImageFile);
    if (validFiles.length === 0) {
      setError('Please upload PDF or image files only.');
      return;
    }

    const file = validFiles[0];
    const fileState: UploadedPrescriptionFile = {
      id: uuidv4(),
      name: file.name,
      size: file.size,
      type: file.type || 'application/octet-stream',
      uploadedAt: new Date(),
      status: 'uploading',
      progress: 0,
    };
    addFile(fileState);
    await uploadPrescriptionFile(file, fileState);
  };

  const getStatusColor = (status: UploadedPrescriptionFile['status'] | string) => {
    switch (status) {
      case 'uploading':
        return '#1976d2';
      case 'processing':
        return '#ff9800';
      case 'completed':
      case 'approved':
        return '#009688';
      case 'error':
      case 'rejected':
        return '#e91e63';
      default:
        return '#1976d2';
    }
  };

  const handlePdfPreview = (uploadId: string) => {
    window.open(prescriptionUploadAPI.getPrescriptionUploadPdfUrl(uploadId), '_blank');
  };

  const handleImagePreview = async (uploadId: string) => {
    try {
      const response = await prescriptionUploadAPI.getPrescriptionUploadImages(uploadId);
      setImageGallery({ open: true, images: response.data.images || [] });
    } catch {
      setError('Failed to load image preview.');
    }
  };

  const handleHtmlPreview = async (uploadId: string) => {
    try {
      const response = await prescriptionUploadAPI.getPrescriptionUploadHtmlPreview(uploadId);
      setPreviewHtml(response.data.html || '');
      setIsPreviewOpen(true);
    } catch {
      setError('Failed to load structured preview.');
    }
  };

  const handleApprove = async () => {
    if (!sessionId || !activeUploadId) {
      setError('Session is required for approving prescription medications.');
      return;
    }
    const validRows = rows
      .map((r) => ({ ...r, drug_name: (r.drug_name || '').trim() }))
      .filter((r) => r.drug_name);
    if (!validRows.length) {
      setError('Please keep at least one medication row with drug name.');
      return;
    }
    setIsApproving(true);
    setError(null);
    try {
      await prescriptionUploadAPI.approvePrescriptionUpload(activeUploadId, {
        session_id: sessionId,
        medications: validRows,
      });
      onApproved(validRows);
      void fetchSessionUploads();
      void fetchPatientUploads();
    } catch {
      setError('Failed to approve and save medications.');
    } finally {
      setIsApproving(false);
    }
  };

  const renderPreviewActions = (uploadId: string) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title="Preview PDF" arrow>
        <IconButton size="small" onClick={() => handlePdfPreview(uploadId)} sx={{ color: '#1976d2' }}>
          <PictureAsPdf fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Preview Images" arrow>
        <IconButton size="small" onClick={() => void handleImagePreview(uploadId)} sx={{ color: '#1976d2' }}>
          <ImageIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Structured HTML Preview" arrow>
        <IconButton size="small" onClick={() => void handleHtmlPreview(uploadId)} sx={{ color: '#1976d2' }}>
          <Preview fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderContextFileItem = (file: UploadedPrescriptionFile) => (
    <ListItem
      key={file.id}
      sx={{
        borderLeft: `4px solid ${getStatusColor(file.status)}`,
        mb: 1,
        bgcolor: '#fafafa',
        borderRadius: 1,
      }}
    >
      <ListItemIcon>
        {file.status === 'completed' ? (
          <CheckCircle sx={{ color: '#009688' }} />
        ) : file.status === 'processing' ? (
          <Psychology sx={{ color: '#ff9800' }} />
        ) : (
          <InsertDriveFile sx={{ color: file.status === 'error' ? '#e91e63' : '#1976d2' }} />
        )}
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body1">{file.name}</Typography>
            <Chip
              label={file.status}
              size="small"
              sx={{
                bgcolor: getStatusColor(file.status),
                color: 'white',
                textTransform: 'capitalize',
              }}
            />
          </Box>
        }
        secondary={
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" display="block">
              {formatFileSize(file.size)} • {file.uploadedAt.toLocaleTimeString()}
            </Typography>
            {(file.status === 'uploading' || file.status === 'processing') && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={file.progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': { bgcolor: getStatusColor(file.status) },
                  }}
                />
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                  {file.status === 'uploading'
                    ? `Uploading... ${file.progress}%`
                    : 'Processing with Vision AI...'}
                </Typography>
              </Box>
            )}
            {file.status === 'completed' && file.confidence != null && (
              <Chip
                label={`Confidence: ${(file.confidence * 100).toFixed(0)}%`}
                size="small"
                sx={{ mt: 1, bgcolor: '#009688', color: 'white' }}
              />
            )}
          </Box>
        }
      />
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {file.upload_id && file.status === 'completed' && (
          <>
            {renderPreviewActions(file.upload_id)}
            <Button size="small" variant="outlined" onClick={() => void loadUploadForReview(file.upload_id!)}>
              Review
            </Button>
          </>
        )}
        <IconButton onClick={() => removeFile(file.id)} sx={{ color: '#e91e63' }}>
          <Delete />
        </IconButton>
      </Box>
    </ListItem>
  );

  const renderHistoryItem = (upload: PrescriptionUploadSummary) => (
    <ListItem
      key={upload.upload_id}
      sx={{
        borderLeft: '4px solid #009688',
        mb: 1,
        bgcolor: '#fafafa',
        borderRadius: 1,
      }}
    >
      <ListItemIcon>
        <InsertDriveFile sx={{ color: '#1976d2' }} />
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body1">{prescriptionDisplayName(upload)}</Typography>
            <Chip
              label={(upload.file_type || 'pdf').toUpperCase()}
              size="small"
              sx={{ bgcolor: '#1976d2', color: 'white' }}
            />
            <Chip
              label={upload.review_status}
              size="small"
              sx={{
                bgcolor: getStatusColor(upload.review_status ?? ''),
                color: 'white',
                textTransform: 'capitalize',
              }}
            />
            <Chip
              label={`${(upload.confidence * 100).toFixed(0)}% confidence`}
              size="small"
              sx={{ bgcolor: '#009688', color: 'white' }}
            />
          </Box>
        }
        secondary={
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            {upload.medication_count} medication(s) •{' '}
            {upload.created_at ? new Date(upload.created_at).toLocaleString() : '—'}
          </Typography>
        }
      />
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {renderPreviewActions(upload.upload_id)}
        <Button size="small" variant="outlined" onClick={() => void loadUploadForReview(upload.upload_id)}>
          Review
        </Button>
      </Box>
    </ListItem>
  );

  return (
    <>
      <DialogTitle>Prescription Upload and Review</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Upload prescription images or PDFs. Review extracted medications and approve to add them to
            the medication list.
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)}>
              <Tab label={`Current Session (${files.length + persistedSessionUploads.length})`} />
              <Tab
                label={`This patient (${patientUploads.length})`}
                icon={
                  <Refresh
                    onClick={(e) => {
                      e.stopPropagation();
                      void fetchPatientUploads();
                    }}
                  />
                }
                iconPosition="end"
              />
            </Tabs>
          </Box>

          {currentTab === 0 && (
            <Box>
              <VisionAIUploadDropzone
                title="Upload Prescription"
                supportedFormatsText={PRESCRIPTION_FORMATS}
                accept={PRESCRIPTION_ACCEPT}
                disabled={isUploading}
                onFilesSelected={handleFiles}
              />

              {files.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Vision AI Processing:</strong> Files are automatically processed using OCR
                    and AI extraction to populate editable medication rows.
                  </Typography>
                </Alert>
              )}

              {(files.length > 0 || persistedSessionUploads.length > 0) && (
                <Paper sx={{ mt: 3, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">
                      Uploaded Files ({files.length + persistedSessionUploads.length})
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<Refresh />}
                      onClick={() => void fetchSessionUploads()}
                      disabled={!sessionId || loadingSession}
                    >
                      Refresh
                    </Button>
                  </Box>
                  {loadingSession && <LinearProgress sx={{ mb: 2 }} />}
                  <List>
                    {files.map(renderContextFileItem)}
                    {persistedSessionUploads.map(renderHistoryItem)}
                  </List>
                </Paper>
              )}

              {!sessionId && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Start a visit session to link uploads to the current session.
                </Alert>
              )}
            </Box>
          )}

          {currentTab === 1 && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Prescriptions for this patient ({patientUploads.length})
                </Typography>
                <Button
                  startIcon={<Refresh />}
                  onClick={() => void fetchPatientUploads()}
                  disabled={loadingPatient || resolvedPatientId == null}
                >
                  Refresh
                </Button>
              </Box>
              {resolvedPatientId == null ? (
                <Alert severity="info">
                  Select a patient and start a visit to see prescription uploads for that patient.
                </Alert>
              ) : loadingPatient ? (
                <LinearProgress />
              ) : patientUploads.length === 0 ? (
                <Alert severity="info">No prescription uploads found for this patient yet.</Alert>
              ) : (
                <List
                  sx={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                  }}
                >
                  {patientUploads.map(renderHistoryItem)}
                </List>
              )}
            </Paper>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {warnings.length > 0 && <Alert severity="warning">{warnings.join(' | ')}</Alert>}

          {activeUploadId && hasRows && (
            <Paper sx={{ p: 2, border: '1px solid #8DBAEA' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Review extracted medications
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Drug Name</TableCell>
                    <TableCell>Dose</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Frequency</TableCell>
                    <TableCell>Route</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Instructions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={`rx-row-${index}`}>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.drug_name || ''}
                          onChange={(e) =>
                            setRows((prev) =>
                              prev.map((item, idx) =>
                                idx === index ? { ...item, drug_name: e.target.value } : item,
                              ),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.dose || ''}
                          onChange={(e) =>
                            setRows((prev) =>
                              prev.map((item, idx) =>
                                idx === index ? { ...item, dose: e.target.value } : item,
                              ),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.unit || ''}
                          onChange={(e) =>
                            setRows((prev) =>
                              prev.map((item, idx) =>
                                idx === index ? { ...item, unit: e.target.value } : item,
                              ),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.frequency || ''}
                          onChange={(e) =>
                            setRows((prev) =>
                              prev.map((item, idx) =>
                                idx === index ? { ...item, frequency: e.target.value } : item,
                              ),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.route || ''}
                          onChange={(e) =>
                            setRows((prev) =>
                              prev.map((item, idx) =>
                                idx === index ? { ...item, route: e.target.value } : item,
                              ),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.duration || ''}
                          onChange={(e) =>
                            setRows((prev) =>
                              prev.map((item, idx) =>
                                idx === index ? { ...item, duration: e.target.value } : item,
                              ),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.medication_instructions || ''}
                          onChange={(e) =>
                            setRows((prev) =>
                              prev.map((item, idx) =>
                                idx === index
                                  ? { ...item, medication_instructions: e.target.value }
                                  : item,
                              ),
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void handleApprove()}
          disabled={!hasRows || !activeUploadId || isApproving}
        >
          {isApproving ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            'Approve and Add to Medication List'
          )}
        </Button>
      </DialogActions>

      <Dialog open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Prescription Structured Preview</DialogTitle>
        <DialogContent dividers>
          <Box dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <ImageGalleryDialog
        open={imageGallery.open}
        reportId={activeUploadId || ''}
        images={imageGallery.images}
        onClose={() => setImageGallery({ open: false, images: [] })}
      />
    </>
  );
}
